// Router en tabbalk. Routes: #/ (lijst, met optionele ?query), #/kaartje, #/fotos, #/betalen/<token>.
// Kaartje en Foto's bestaan alleen als er afbeeldingen zijn; anders valt de route terug op de lijst.
import { h } from './dom.ts';

export type View = 'kaartje' | 'lijst' | 'fotos';
export type Route =
  | { kind: 'pay'; token: string }
  | { kind: 'view'; view: View; params: URLSearchParams };
export type Available = { cards: boolean; photos: boolean };

const PAY_ROUTE = /^#\/betalen\/([A-Za-z0-9_-]+)$/;

const TABS: { view: View; label: string; panel: string }[] = [
  { view: 'kaartje', label: 'Kaartje', panel: 'cardView' },
  { view: 'lijst', label: 'Lijst', panel: 'listView' },
  { view: 'fotos', label: "Foto's", panel: 'photoView' },
];

/** Een hash die geen route is (zoals #main van de skiplink) is geen navigatie. */
export function isRouteHash(hash: string): boolean {
  return hash === '' || hash === '#' || hash.startsWith('#/');
}

export function parseRoute(hash: string): Route {
  const pay = PAY_ROUTE.exec(hash);
  if (pay) return { kind: 'pay', token: pay[1] };
  const raw = hash.replace(/^#/, '');
  const q = raw.indexOf('?');
  const path = (q === -1 ? raw : raw.slice(0, q)).replace(/\/+$/, '');
  const params = new URLSearchParams(q === -1 ? '' : raw.slice(q + 1));
  const view: View = path === '/kaartje' ? 'kaartje' : path === '/fotos' ? 'fotos' : 'lijst';
  return { kind: 'view', view, params };
}

/** Een tabblad zonder inhoud bestaat niet: dan toont de route de lijst. */
export function availableView(view: View, has: Available): View {
  if (view === 'kaartje' && !has.cards) return 'lijst';
  if (view === 'fotos' && !has.photos) return 'lijst';
  return view;
}

export function panelId(view: View): string {
  return TABS.find((t) => t.view === view)?.panel ?? 'listView';
}

function hrefFor(view: View, listHref: string): string {
  return view === 'lijst' ? listHref : `#/${view}`;
}

/** De tabbladen die er zijn, in volgorde. Eén tabblad = geen tabbalk (dan toont de header het merk). */
export function shownViews(has: Available): View[] {
  return TABS.filter((t) => availableView(t.view, has) === t.view).map((t) => t.view);
}

/** Tekent de pillen. `current` is null op het betaalscherm (geen tab actief). */
export function renderTabs(nav: HTMLElement, current: View | null, has: Available, listHref: string): void {
  const views = shownViews(has);
  nav.hidden = views.length < 2;
  const brand = document.getElementById('brand');
  if (brand) brand.hidden = !nav.hidden;
  const shown = TABS.filter((t) => views.includes(t.view));
  const focused = document.activeElement instanceof HTMLElement && nav.contains(document.activeElement)
    ? document.activeElement.dataset.view : undefined;
  nav.replaceChildren(...shown.map((t) => {
    const selected = t.view === current;
    const tab = h('a', {
      role: 'tab', id: `tab-${t.view}`, class: 'tab', href: hrefFor(t.view, listHref),
      'aria-selected': String(selected), 'aria-controls': t.panel,
      tabindex: selected || (current === null && t.view === 'lijst') ? '0' : '-1',
    }, t.label);
    tab.dataset.view = t.view;
    return tab;
  }));
  if (focused) nav.querySelector<HTMLElement>(`[data-view="${focused}"]`)?.focus();
}

/** Pijltjestoetsen en Home/End tussen de tabs (WAI-ARIA tabs-patroon, met automatische activatie). */
export function initTabKeys(nav: HTMLElement): void {
  nav.addEventListener('keydown', (e) => {
    const tabs = [...nav.querySelectorAll<HTMLAnchorElement>('[role="tab"]')];
    const i = tabs.findIndex((t) => t === document.activeElement);
    if (i === -1) return;
    const keys: Record<string, number> = { ArrowLeft: i - 1, ArrowRight: i + 1, Home: 0, End: tabs.length - 1 };
    if (!(e.key in keys)) return;
    e.preventDefault();
    const next = tabs[(keys[e.key] + tabs.length) % tabs.length];
    next.focus();
    next.click();
  });
}
